/**
 * LatestMentionsTemplate - Intelligence Dashboard for High-Signal Mentions
 *
 * A real-time intelligence product designed for DOJ, Congress, and national security analysts.
 * Features:
 * - Timeline-based layout grouped by year (2026 → 2024)
 * - Pattern Detection Insight summary panel
 * - Filterable by Year, Risk Level, and Category
 * - Hover tooltips with summary and risk takeaway
 * - Dark mode with red accents (intelligence dashboard aesthetic)
 */
import { FC, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SecurityIcon from '@mui/icons-material/Security';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SchoolIcon from '@mui/icons-material/School';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import PublicIcon from '@mui/icons-material/Public';
import DescriptionIcon from '@mui/icons-material/Description';
import FilterListIcon from '@mui/icons-material/FilterList';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  MentionCategory,
  MentionEntry,
  RiskLevel,
  getAllCategories,
  getAllRiskLevels,
  getAllYears,
  getMentionsByYear,
  mentionsData,
} from './mentionsData';

// ═══════════════════════════════════════════════════════════════════════════════
// STYLING CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const RISK_COLORS: Record<RiskLevel, string> = {
  Critical: '#ef4444', // Red-500
  High: '#f97316', // Orange-500
  Moderate: '#eab308', // Yellow-500
};

const CATEGORY_COLORS: Record<MentionCategory, string> = {
  Congressional: '#dc2626', // Red-600
  Funding: '#ea580c', // Orange-600
  University: '#2563eb', // Blue-600
  Defense: '#7c3aed', // Violet-600
  Influence: '#0891b2', // Cyan-600
  Report: '#059669', // Emerald-600
};

const CATEGORY_ICONS: Record<MentionCategory, React.ReactElement> = {
  Congressional: <AccountBalanceIcon sx={{ fontSize: 16 }} />,
  Funding: <TrendingUpIcon sx={{ fontSize: 16 }} />,
  University: <SchoolIcon sx={{ fontSize: 16 }} />,
  Defense: <MilitaryTechIcon sx={{ fontSize: 16 }} />,
  Influence: <PublicIcon sx={{ fontSize: 16 }} />,
  Report: <DescriptionIcon sx={{ fontSize: 16 }} />,
};

// ═══════════════════════════════════════════════════════════════════════════════
// PATTERN DETECTION PANEL
// ═══════════════════════════════════════════════════════════════════════════════

interface PatternInsight {
  icon: React.ReactNode;
  title: string;
  description: string;
  count: number;
  trend: 'up' | 'stable' | 'down';
}

const PatternDetectionPanel: FC = () => {
  const insights: PatternInsight[] = useMemo(() => {
    const congressionalCount = mentionsData.filter(
      (m) => m.category === 'Congressional',
    ).length;
    const fundingCount = mentionsData.filter(
      (m) => m.category === 'Funding',
    ).length;
    const universityCount = mentionsData.filter(
      (m) => m.category === 'University',
    ).length;
    const criticalCount = mentionsData.filter(
      (m) => m.riskLevel === 'Critical',
    ).length;

    return [
      {
        icon: <SecurityIcon sx={{ color: '#ef4444' }} />,
        title: 'Systemic Research Security Failures',
        description:
          'Pattern detected across DOE national laboratories, universities, and federal contractors. Institutional compliance breakdown enables unauthorized technology transfer.',
        count: congressionalCount + universityCount,
        trend: 'up' as const,
      },
      {
        icon: <TrendingUpIcon sx={{ color: '#f97316' }} />,
        title: 'U.S. Funding Exposure to PRC-Linked Entities',
        description:
          'Over $1B in federal research funding identified flowing to collaborative projects with PRC-linked laboratories. Pentagon and DOE research on sanctioned supercomputers.',
        count: fundingCount,
        trend: 'up' as const,
      },
      {
        icon: <SchoolIcon sx={{ color: '#2563eb' }} />,
        title: 'Institutional Compliance Breakdown',
        description:
          'Section 117 foreign gift reporting failures widespread. Elite capture dynamics documented at leading research universities including Stanford and Texas A&M.',
        count: universityCount,
        trend: 'stable' as const,
      },
      {
        icon: <WarningAmberIcon sx={{ color: '#ef4444' }} />,
        title: 'Critical Threat Indicators',
        description:
          'High concentration of Critical-rated intelligence spanning defense contractor penetration, nuclear research exposure, and strategic asset proximity concerns.',
        count: criticalCount,
        trend: 'up' as const,
      },
    ];
  }, []);

  return (
    <Paper
      elevation={0}
      sx={{
        background:
          'linear-gradient(135deg, rgba(127, 29, 29, 0.15) 0%, rgba(30, 41, 59, 0.8) 100%)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: 3,
        p: 3,
        mb: 4,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: 'rgba(239, 68, 68, 0.2)',
            border: '1px solid rgba(239, 68, 68, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SecurityIcon sx={{ color: '#ef4444', fontSize: 22 }} />
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: '#ef4444',
            }}
          >
            Pattern Detection
          </Typography>
          <Typography
            sx={{
              fontSize: '1.15rem',
              fontWeight: 700,
              color: '#f1f5f9',
              lineHeight: 1.2,
            }}
          >
            Intelligence Summary
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={2.5}>
        {insights.map((insight, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Box
              sx={{
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid rgba(148, 163, 184, 0.12)',
                borderRadius: 2,
                p: 2,
                height: '100%',
                transition: 'border-color 0.2s',
                '&:hover': {
                  borderColor: 'rgba(239, 68, 68, 0.4)',
                },
              }}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                <Box sx={{ mt: 0.25 }}>{insight.icon}</Box>
                <Box sx={{ flex: 1 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Typography
                      sx={{
                        fontSize: '0.92rem',
                        fontWeight: 700,
                        color: '#f1f5f9',
                        lineHeight: 1.3,
                        mb: 0.75,
                      }}
                    >
                      {insight.title}
                    </Typography>
                    <Chip
                      label={insight.count}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        backgroundColor: 'rgba(239, 68, 68, 0.2)',
                        color: '#fca5a5',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        ml: 1,
                      }}
                    />
                  </Stack>
                  <Typography
                    sx={{
                      fontSize: '0.8rem',
                      color: '#94a3b8',
                      lineHeight: 1.5,
                    }}
                  >
                    {insight.description}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MENTION CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

interface MentionCardProps {
  mention: MentionEntry;
}

const MentionCard: FC<MentionCardProps> = ({ mention }) => {
  const formattedDate = new Date(mention.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const handleOpenSource = () => {
    window.open(mention.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Tooltip
      title={
        <Box sx={{ p: 1, maxWidth: 400 }}>
          <Typography
            sx={{
              fontSize: '0.82rem',
              fontWeight: 600,
              color: '#f1f5f9',
              mb: 1,
            }}
          >
            Summary
          </Typography>
          <Typography
            sx={{
              fontSize: '0.78rem',
              color: '#cbd5e1',
              mb: 1.5,
              lineHeight: 1.5,
            }}
          >
            {mention.summary}
          </Typography>
          <Box
            sx={{
              background: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 1,
              p: 1.25,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.72rem',
                fontWeight: 700,
                color: '#fca5a5',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                mb: 0.5,
              }}
            >
              Key Risk Takeaway
            </Typography>
            <Typography
              sx={{ fontSize: '0.78rem', color: '#fecaca', lineHeight: 1.45 }}
            >
              {mention.keyRiskTakeaway}
            </Typography>
          </Box>
        </Box>
      }
      placement="right"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: 'rgba(15, 23, 42, 0.98)',
            border: '1px solid rgba(148, 163, 184, 0.2)',
            borderRadius: 2,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            maxWidth: 440,
          },
        },
        arrow: {
          sx: {
            color: 'rgba(15, 23, 42, 0.98)',
          },
        },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          background: 'rgba(15, 23, 42, 0.6)',
          border: '1px solid rgba(148, 163, 184, 0.12)',
          borderLeft: `3px solid ${RISK_COLORS[mention.riskLevel]}`,
          borderRadius: 2,
          p: 2,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            background: 'rgba(30, 41, 59, 0.7)',
            borderColor: 'rgba(148, 163, 184, 0.25)',
            transform: 'translateX(4px)',
          },
        }}
      >
        <Stack spacing={1}>
          {/* Top row: Date + Risk + Category */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                sx={{
                  fontSize: '0.72rem',
                  color: '#cbd5e1',
                  fontWeight: 500,
                  letterSpacing: '0.02em',
                }}
              >
                {formattedDate}
              </Typography>
              <Chip
                label={mention.riskLevel}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  backgroundColor: `${RISK_COLORS[mention.riskLevel]}22`,
                  color: RISK_COLORS[mention.riskLevel],
                  border: `1px solid ${RISK_COLORS[mention.riskLevel]}44`,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Chip
                icon={CATEGORY_ICONS[mention.category]}
                label={mention.category}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  backgroundColor: `${CATEGORY_COLORS[mention.category]}22`,
                  color: CATEGORY_COLORS[mention.category],
                  border: `1px solid ${CATEGORY_COLORS[mention.category]}44`,
                  '& .MuiChip-icon': {
                    color: CATEGORY_COLORS[mention.category],
                  },
                }}
              />
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenSource();
                }}
                sx={{
                  color: '#64748b',
                  '&:hover': {
                    color: '#94a3b8',
                    backgroundColor: 'rgba(148, 163, 184, 0.1)',
                  },
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
          </Stack>

          {/* Title */}
          <Typography
            onClick={handleOpenSource}
            sx={{
              fontSize: '0.95rem',
              fontWeight: 600,
              color: '#e2e8f0',
              lineHeight: 1.4,
              cursor: 'pointer',
              '&:hover': {
                color: '#f1f5f9',
                textDecoration: 'underline',
              },
            }}
          >
            {mention.title}
          </Typography>

          {/* Source */}
          <Typography
            sx={{
              fontSize: '0.78rem',
              color: '#cbd5e1',
              fontWeight: 500,
            }}
          >
            {mention.source}
          </Typography>
        </Stack>
      </Paper>
    </Tooltip>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// YEAR SECTION COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

interface YearSectionProps {
  year: number;
  mentions: MentionEntry[];
}

const YearSection: FC<YearSectionProps> = ({ year, mentions }) => {
  const criticalCount = mentions.filter(
    (m) => m.riskLevel === 'Critical',
  ).length;
  const highCount = mentions.filter((m) => m.riskLevel === 'High').length;

  return (
    <Box sx={{ mb: 4 }}>
      {/* Year header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography
            sx={{
              fontSize: '1.5rem',
              fontWeight: 800,
              color: '#64748b',
              fontFamily: 'monospace',
              letterSpacing: '-0.02em',
            }}
          >
            {year}
          </Typography>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: 'rgba(148, 163, 184, 0.2)', my: 0.5 }}
          />
          <Typography
            sx={{
              fontSize: '0.82rem',
              color: '#64748b',
              fontWeight: 500,
            }}
          >
            {`${mentions.length} intelligence item${
              mentions.length !== 1 ? 's' : ''
            }`}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          {criticalCount > 0 && (
            <Chip
              label={`${criticalCount} Critical`}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 700,
                backgroundColor: 'rgba(239, 68, 68, 0.15)',
                color: '#fca5a5',
                border: '1px solid rgba(239, 68, 68, 0.3)',
              }}
            />
          )}
          {highCount > 0 && (
            <Chip
              label={`${highCount} High`}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 700,
                backgroundColor: 'rgba(249, 115, 22, 0.15)',
                color: '#fdba74',
                border: '1px solid rgba(249, 115, 22, 0.3)',
              }}
            />
          )}
        </Stack>
      </Stack>

      {/* Timeline connector */}
      <Box
        sx={{
          position: 'relative',
          pl: 3,
          borderLeft: '2px solid rgba(239, 68, 68, 0.3)',
          ml: 1,
        }}
      >
        <Stack spacing={1.5}>
          {mentions.map((mention) => (
            <Box
              key={mention.id}
              sx={{
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: -21,
                  top: 14,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: RISK_COLORS[mention.riskLevel],
                  border: '2px solid rgba(15, 23, 42, 0.9)',
                },
              }}
            >
              <MentionCard mention={mention} />
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// FILTERS COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

interface FiltersProps {
  selectedYear: number | 'all';
  selectedRiskLevel: RiskLevel | 'all';
  selectedCategory: MentionCategory | 'all';
  onYearChange: (year: number | 'all') => void;
  onRiskLevelChange: (riskLevel: RiskLevel | 'all') => void;
  onCategoryChange: (category: MentionCategory | 'all') => void;
}

const Filters: FC<FiltersProps> = ({
  selectedYear,
  selectedRiskLevel,
  selectedCategory,
  onYearChange,
  onRiskLevelChange,
  onCategoryChange,
}) => {
  const years = getAllYears();
  const riskLevels = getAllRiskLevels();
  const categories = getAllCategories();

  const selectStyles = {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(148, 163, 184, 0.2)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(148, 163, 184, 0.4)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(239, 68, 68, 0.6)',
    },
    '& .MuiSelect-select': {
      color: '#e2e8f0',
      fontSize: '0.85rem',
    },
    '& .MuiSvgIcon-root': {
      color: '#64748b',
    },
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    borderRadius: 2,
  };

  const labelStyles = {
    color: '#94a3b8',
    fontSize: '0.82rem',
    '&.Mui-focused': {
      color: '#ef4444',
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid rgba(148, 163, 184, 0.12)',
        borderRadius: 2,
        p: 2,
        mb: 3,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={2}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <FilterListIcon sx={{ color: '#64748b', fontSize: 20 }} />
          <Typography
            sx={{
              fontSize: '0.82rem',
              fontWeight: 600,
              color: '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}
          >
            Filters
          </Typography>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ flex: 1 }}
        >
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel sx={labelStyles}>Year</InputLabel>
            <Select
              value={selectedYear.toString()}
              label="Year"
              onChange={(e: SelectChangeEvent) =>
                onYearChange(
                  e.target.value === 'all'
                    ? 'all'
                    : parseInt(e.target.value, 10),
                )
              }
              sx={selectStyles}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'rgba(15, 23, 42, 0.98)',
                    border: '1px solid rgba(148, 163, 184, 0.2)',
                    '& .MuiMenuItem-root': {
                      color: '#e2e8f0',
                      fontSize: '0.85rem',
                      '&:hover': {
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(239, 68, 68, 0.2)',
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="all">All Years</MenuItem>
              {years.map((year) => (
                <MenuItem key={year} value={year.toString()}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={labelStyles}>Risk Level</InputLabel>
            <Select
              value={selectedRiskLevel}
              label="Risk Level"
              onChange={(e: SelectChangeEvent) =>
                onRiskLevelChange(e.target.value as RiskLevel | 'all')
              }
              sx={selectStyles}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'rgba(15, 23, 42, 0.98)',
                    border: '1px solid rgba(148, 163, 184, 0.2)',
                    '& .MuiMenuItem-root': {
                      color: '#e2e8f0',
                      fontSize: '0.85rem',
                      '&:hover': {
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(239, 68, 68, 0.2)',
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="all">All Levels</MenuItem>
              {riskLevels.map((level) => (
                <MenuItem key={level} value={level}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: RISK_COLORS[level],
                      }}
                    />
                    <span>{level}</span>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={labelStyles}>Category</InputLabel>
            <Select
              value={selectedCategory}
              label="Category"
              onChange={(e: SelectChangeEvent) =>
                onCategoryChange(e.target.value as MentionCategory | 'all')
              }
              sx={selectStyles}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'rgba(15, 23, 42, 0.98)',
                    border: '1px solid rgba(148, 163, 184, 0.2)',
                    '& .MuiMenuItem-root': {
                      color: '#e2e8f0',
                      fontSize: '0.85rem',
                      '&:hover': {
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(239, 68, 68, 0.2)',
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {CATEGORY_ICONS[category]}
                    <span>{category}</span>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>
    </Paper>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN TEMPLATE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export const LatestMentionsTemplate: FC = () => {
  const [selectedYear, setSelectedYear] = useState<number | 'all'>('all');
  const [selectedRiskLevel, setSelectedRiskLevel] = useState<RiskLevel | 'all'>(
    'all',
  );
  const [selectedCategory, setSelectedCategory] = useState<
    MentionCategory | 'all'
  >('all');

  const filteredMentionsByYear = useMemo(() => {
    const allByYear = getMentionsByYear();
    const filtered = new Map<number, MentionEntry[]>();

    allByYear.forEach((mentions, year) => {
      // Filter by year
      if (selectedYear !== 'all' && year !== selectedYear) {
        return;
      }

      // Filter by risk level and category
      const yearFiltered = mentions.filter((mention) => {
        if (
          selectedRiskLevel !== 'all' &&
          mention.riskLevel !== selectedRiskLevel
        ) {
          return false;
        }
        if (
          selectedCategory !== 'all' &&
          mention.category !== selectedCategory
        ) {
          return false;
        }
        return true;
      });

      if (yearFiltered.length > 0) {
        filtered.set(year, yearFiltered);
      }
    });

    return filtered;
  }, [selectedYear, selectedRiskLevel, selectedCategory]);

  const totalFilteredCount = useMemo(() => {
    let count = 0;
    filteredMentionsByYear.forEach((mentions) => {
      count += mentions.length;
    });
    return count;
  }, [filteredMentionsByYear]);

  const criticalFindings = useMemo(() => {
    let count = 0;
    filteredMentionsByYear.forEach((mentions) => {
      count += mentions.filter(
        (m) =>
          m.riskLevel === 'Critical' &&
          (m.category === 'Congressional' || m.category === 'Funding'),
      ).length;
    });
    return count;
  }, [filteredMentionsByYear]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background:
                'linear-gradient(135deg, rgba(239, 68, 68, 0.3) 0%, rgba(127, 29, 29, 0.4) 100%)',
              border: '1px solid rgba(239, 68, 68, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SecurityIcon sx={{ color: '#ef4444', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: '#ef4444',
              }}
            >
              Intelligence Dashboard
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: '#64748b',
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}
            >
              Mentions
            </Typography>
          </Box>
        </Stack>
        <Typography
          sx={{
            fontSize: '0.95rem',
            color: '#94a3b8',
            maxWidth: 800,
            lineHeight: 1.6,
            mt: 1.5,
          }}
        >
          High-signal intelligence tracking congressional investigations,
          federal funding exposure, university research security failures,
          defense contractor risks, and strategic influence operations.
        </Typography>
      </Box>

      {/* Pattern Detection Panel */}
      <PatternDetectionPanel />

      {/* Filters */}
      <Filters
        selectedYear={selectedYear}
        selectedRiskLevel={selectedRiskLevel}
        selectedCategory={selectedCategory}
        onYearChange={setSelectedYear}
        onRiskLevelChange={setSelectedRiskLevel}
        onCategoryChange={setSelectedCategory}
      />

      {/* Results summary */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>
          <Box component="span">{'Showing '}</Box>
          <Box component="span" sx={{ color: '#e2e8f0', fontWeight: 600 }}>
            {totalFilteredCount}
          </Box>
          <Box component="span">
            {` intelligence item${totalFilteredCount !== 1 ? 's' : ''}`}
          </Box>
        </Typography>
        {criticalFindings > 0 && (
          <Chip
            icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
            label={`${criticalFindings} Critical Finding${
              criticalFindings !== 1 ? 's' : ''
            }`}
            size="small"
            sx={{
              height: 26,
              fontSize: '0.75rem',
              fontWeight: 700,
              backgroundColor: 'rgba(239, 68, 68, 0.15)',
              color: '#fca5a5',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              '& .MuiChip-icon': {
                color: '#fca5a5',
              },
            }}
          />
        )}
      </Stack>

      {/* Timeline by year */}
      {Array.from(filteredMentionsByYear.entries()).map(([year, mentions]) => (
        <YearSection key={year} year={year} mentions={mentions} />
      ))}

      {/* Empty state */}
      {filteredMentionsByYear.size === 0 && (
        <Paper
          elevation={0}
          sx={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid rgba(148, 163, 184, 0.12)',
            borderRadius: 2,
            p: 6,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>
            No intelligence items match the current filters.
          </Typography>
        </Paper>
      )}

      {/* Footer */}
      <Box
        sx={{ mt: 6, pt: 3, borderTop: '1px solid rgba(148, 163, 184, 0.12)' }}
      >
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: '#475569',
            textAlign: 'center',
            letterSpacing: '0.02em',
          }}
        >
          Intelligence curated by Data Abyss • Sources include congressional
          reports, federal investigations, and open-source intelligence
        </Typography>
      </Box>
    </Container>
  );
};

export default LatestMentionsTemplate;
